import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { storage } from "../../state/storage.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";
import {
  createPreset,
  exportPresetToJson,
  importPresetFromJson,
  type Preset,
  type PromptBlock,
  type ReasoningLevel,
  type SamplingParams,
  type TokenizerMode,
} from "@roqeet/core";

/**
 * 프리셋 관리(결정 #12: 단계형 프롬프트 파이프라인 + 샘플러). 프리셋은 샘플링
 * 파라미터 + 순서 있는 프롬프트 블록을 담는다. 여기서 만든/고른 프리셋이
 * buildPrompt(결정 #12)의 조립 순서와 토큰 예산을 결정한다.
 *
 * 블록 토글/순서 변경이 그대로 프롬프트 조립 순서에 반영된다(위→아래).
 */
const BLOCK_LABEL_KEY: Record<PromptBlock["type"], TKey> = {
  system: "block.system",
  persona: "block.persona",
  "char-description": "block.charDescription",
  scenario: "block.scenario",
  "example-dialogue": "block.exampleDialogue",
  "lorebook-before": "block.lorebookBefore",
  "lorebook-after": "block.lorebookAfter",
  "chat-history": "block.chatHistory",
  "author-note": "block.authorNote",
  "post-history": "block.postHistory",
  custom: "block.custom",
};

export function PresetSection() {
  const { t } = useT();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [settings, setSettings] = useState<Awaited<ReturnType<typeof storage.getSettings>>>();
  const [editing, setEditing] = useState<Preset | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reload = async () => {
    const [list, s] = await Promise.all([storage.presets.list(), storage.getSettings()]);
    setPresets(list);
    setSettings(s);
    setEditing((cur) => (cur ? (list.find((p) => p.id === cur.id) ?? null) : null));
  };
  useEffect(() => {
    void reload();
  }, []);

  const createNew = async () => {
    const p = createPreset({ name: t("preset.newName") });
    await storage.presets.put(p);
    await reload();
    setEditing(p);
  };

  // 프리셋을 단독 JSON으로 내보내기(결정 #6/#23).
  const exportPreset = async (p: Preset) => {
    const blob = new Blob([exportPresetToJson(p)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = p.name.replace(/[^\w가-힣\- ]/g, "_").slice(0, 60) || "preset";
    a.href = url;
    a.download = `${safe}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // JSON 파일에서 프리셋 가져오기(결정 #6).
  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;
    setNotice(null);
    let ok = 0;
    const errors: string[] = [];
    for (const file of files) {
      try {
        const p = importPresetFromJson(await file.text());
        await storage.presets.put(p);
        ok++;
      } catch (err) {
        errors.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    await reload();
    setNotice([t("preset.imported", { n: ok }), ...errors].join(" / ").slice(0, 300));
  };

  const setDefault = async (p: Preset) => {
    const current = await storage.getSettings();
    await storage.putSettings({ ...current, defaultPresetId: p.id } as any);
    await reload();
  };

  const remove = async (p: Preset) => {
    if (!confirm(t("preset.deleteConfirm", { name: p.name }))) return;
    await storage.presets.delete(p.id);
    // 마지막 프리셋을 지우면 앱이 0개가 되어 프롬프트 조립이 깨진다(결정 #12). 그래서
    // 삭제는 항상 허용하되, 비면 기본 프리셋을 자동 재생성한다.
    const remaining = await storage.presets.list();
    if (remaining.length === 0) {
      const fresh = createPreset({ name: "Default" });
      await storage.presets.put(fresh);
    }
    await reload();
  };

  if (editing) {
    return <PresetEditor preset={editing} onBack={() => setEditing(null)} onChange={reload} />;
  }

  return (
    <section>
      <h2 className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-ink-faint">
        <span>{t("preset.title")}</span>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          multiple
          className="hidden"
          onChange={onImport}
        />
        <button
          className="rounded border border-line px-2 py-0.5 text-xs font-normal normal-case text-ink-soft hover:bg-bg-raised"
          onClick={() => fileRef.current?.click()}
        >
          {t("common.import")}
        </button>
      </h2>
      <div className="space-y-2">
        {notice && (
          <div className="flex items-start justify-between gap-2 rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-xs text-ink-soft">
            <span className="min-w-0 break-words">{notice}</span>
            <button className="shrink-0 text-ink-faint hover:text-ink" onClick={() => setNotice(null)}>
              ✕
            </button>
          </div>
        )}
        <ul className="space-y-2">
          {presets.map((p) => {
            const isDefault = settings?.defaultPresetId === p.id;
            return (
              <li
                key={p.id}
                className="flex items-start justify-between gap-2 rounded-xl border border-line bg-bg-soft px-3 py-2 sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="truncate text-sm text-ink">{p.name}</span>
                    {isDefault && (
                      <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs text-accent-soft">
                        {t("common.default")}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-ink-faint">
                    {p.apiStyle} · temp {p.sampling.temperature ?? "-"} · ctx{" "}
                    {p.sampling.contextSize ?? "-"}
                  </div>
                </div>
                <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-1">
                  {!isDefault && (
                    <button
                      className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                      onClick={() => setDefault(p)}
                    >
                      {t("common.default")}
                    </button>
                  )}
                  <button
                    className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                    onClick={() => setEditing(p)}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                    onClick={() => exportPreset(p)}
                  >
                    {t("common.export")}
                  </button>
                  <button
                    className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                    onClick={() => remove(p)}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <button
          className="w-full rounded-xl border border-dashed border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={createNew}
        >
          {t("preset.add")}
        </button>
      </div>
    </section>
  );
}

function PresetEditor({
  preset,
  onBack,
  onChange,
}: {
  preset: Preset;
  onBack: () => void;
  onChange: () => void | Promise<void>;
}) {
  const { t } = useT();
  const [name, setName] = useState(preset.name);
  const [sampling, setSampling] = useState<SamplingParams>(preset.sampling);
  const [blocks, setBlocks] = useState<PromptBlock[]>(preset.blocks);
  // 드래그 재정렬 상태(결정 #11 모바일 친화): 길게 눌러 시작 → 끌어서 위치 변경.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = async (next?: { blocks?: PromptBlock[]; sampling?: SamplingParams; name?: string }) => {
    const merged: Preset = {
      ...preset,
      name: next?.name ?? name,
      sampling: next?.sampling ?? sampling,
      blocks: next?.blocks ?? blocks,
    };
    await storage.presets.put(merged);
    await onChange();
  };

  const num = (key: keyof SamplingParams, label: string, step = 0.1) => (
    <label className="flex items-center justify-between gap-2 py-1">
      <span className="text-sm text-ink-soft">{label}</span>
      <input
        type="number"
        step={step}
        className="w-24 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
        defaultValue={sampling[key] as number | undefined}
        onBlur={(e) => {
          const v = e.target.value === "" ? undefined : Number(e.target.value);
          const nextSampling = { ...sampling, [key]: v };
          setSampling(nextSampling);
          void save({ sampling: nextSampling });
        }}
      />
    </label>
  );

  const setSamplingKey = <K extends keyof SamplingParams>(key: K, value: SamplingParams[K]) => {
    const nextSampling = { ...sampling, [key]: value };
    if (value === undefined) delete nextSampling[key];
    setSampling(nextSampling);
    void save({ sampling: nextSampling });
  };

  const moveBlock = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    setBlocks(next);
    void save({ blocks: next });
  };

  const toggleBlock = (id: string) => {
    const next = blocks.map((b) => (b.id === id ? { ...b, enabled: !b.enabled } : b));
    setBlocks(next);
    void save({ blocks: next });
  };

  /** 인덱스 from → to로 블록을 옮긴다(드래그 종료 시 저장). */
  const reorder = (from: number, to: number) => {
    if (from === to || to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    setBlocks(next);
    void save({ blocks: next });
  };

  // 길게 눌러 드래그 시작(모바일 친화, 결정 #11). 200ms 누르면 활성화.
  const startLongPress = (index: number) => {
    clearLongPress();
    longPress.current = setTimeout(() => setDragIndex(index), 200);
  };
  const clearLongPress = () => {
    if (longPress.current) {
      clearTimeout(longPress.current);
      longPress.current = null;
    }
  };

  // 드래그 중 포인터 위치 아래의 항목 인덱스를 계산해 실시간으로 재배치.
  const onPointerMove = (e: ReactPointerEvent) => {
    if (dragIndex === null || !listRef.current) return;
    e.preventDefault();
    const items = Array.from(listRef.current.querySelectorAll("[data-block-row]"));
    const y = e.clientY;
    let target = dragIndex;
    for (let i = 0; i < items.length; i++) {
      const rect = items[i]!.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        target = i;
        break;
      }
      if (y < rect.top) {
        target = i;
        break;
      }
      target = i; // 마지막보다 아래면 끝으로.
    }
    if (target !== dragIndex) {
      reorder(dragIndex, target);
      setDragIndex(target);
    }
  };

  const endDrag = () => {
    clearLongPress();
    setDragIndex(null);
  };

  return (
    <div className="space-y-3 rounded-xl border border-line bg-bg-soft p-3">
      <div className="flex items-center gap-2">
        <button className="rounded p-1 text-ink-soft hover:bg-bg-raised" onClick={onBack}>
          {t("preset.backToList")}
        </button>
        <input
          className="flex-1 rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => save({ name })}
        />
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold text-ink-faint">{t("preset.sampling")}</h3>
        <div className="divide-y divide-line rounded-lg border border-line px-3">
          {num("temperature", "Temperature")}
          {num("topP", "Top P")}
          {num("maxTokens", t("preset.maxTokens"), 1)}
          {num("contextSize", t("preset.contextSize"), 1)}
          <label className="flex items-center justify-between gap-2 py-1">
            <span className="text-sm text-ink-soft">Reasoning</span>
            <select
              className="w-32 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink"
              value={sampling.reasoningLevel ?? "auto"}
              onChange={(e) =>
                setSamplingKey("reasoningLevel", e.target.value as ReasoningLevel)
              }
            >
              <option value="auto">Auto</option>
              <option value="off">Off</option>
              <option value="minimal">Minimal</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="xhigh">XHigh</option>
            </select>
          </label>
          {num("reasoningBudgetTokens", "Reasoning budget", 1)}
          <label className="flex items-center justify-between gap-2 py-1">
            <span className="text-sm text-ink-soft">Tokenizer</span>
            <select
              className="w-36 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink"
              value={sampling.tokenizer ?? "auto"}
              onChange={(e) => setSamplingKey("tokenizer", e.target.value as TokenizerMode)}
            >
              <option value="auto">Auto</option>
              <option value="heuristic">Heuristic</option>
              <option value="o200k_base">o200k_base</option>
              <option value="cl100k_base">cl100k_base</option>
              <option value="p50k_base">p50k_base</option>
              <option value="r50k_base">r50k_base</option>
              <option value="gpt2">gpt2</option>
            </select>
          </label>
          {num("priceInputPerMTok", "Input $/1M", 0.01)}
          {num("priceOutputPerMTok", "Output $/1M", 0.01)}
          {num("priceReasoningPerMTok", "Reasoning $/1M", 0.01)}
          {num("frequencyPenalty", "Frequency Penalty")}
          {num("presencePenalty", "Presence Penalty")}
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold text-ink-faint">
          {t("preset.blocksTitle")}
        </h3>
        <ul
          ref={listRef}
          className="space-y-1"
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
        >
          {blocks.map((block, i) => (
            <li
              key={block.id}
              data-block-row
              className={[
                "flex items-center gap-2 rounded-lg border bg-bg-raised px-2 py-1.5 transition-colors",
                dragIndex === i ? "border-accent bg-bg-soft" : "border-line",
              ].join(" ")}
            >
              {/* 드래그 손잡이: 길게 눌러 이동(모바일). touch-none으로 스크롤과 충돌 방지. */}
              <button
                className="shrink-0 cursor-grab touch-none px-1 text-ink-faint hover:text-ink active:cursor-grabbing"
                aria-label={t("preset.dragHandle")}
                title={t("preset.dragHandleTitle")}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  startLongPress(i);
                }}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                ⠿
              </button>
              <div className="flex flex-col">
                <button
                  className="text-xs text-ink-faint hover:text-ink disabled:opacity-30"
                  onClick={() => moveBlock(i, -1)}
                  disabled={i === 0}
                  aria-label={t("common.moveUp")}
                >
                  ▲
                </button>
                <button
                  className="text-xs text-ink-faint hover:text-ink disabled:opacity-30"
                  onClick={() => moveBlock(i, 1)}
                  disabled={i === blocks.length - 1}
                  aria-label={t("common.moveDown")}
                >
                  ▼
                </button>
              </div>
              <span className="flex-1 text-sm text-ink">
                {t(BLOCK_LABEL_KEY[block.type]) ?? block.type}
                {block.label && block.label !== block.type && (
                  <span className="ml-1 text-xs text-ink-faint">({block.label})</span>
                )}
              </span>
              <button
                role="switch"
                aria-checked={block.enabled}
                aria-label={t("preset.toggleUse", { name: t(BLOCK_LABEL_KEY[block.type]) })}
                onClick={() => toggleBlock(block.id)}
                className={[
                  "relative shrink-0 rounded-full transition-colors",
                  block.enabled ? "bg-accent" : "bg-bg-raised",
                ].join(" ")}
                style={{ width: 36, height: 20 }}
              >
                <span
                  className={[
                    "absolute rounded-full transition-transform",
                    block.enabled ? "bg-white" : "border border-line bg-bg-soft shadow-sm",
                  ].join(" ")}
                  style={{
                    width: 16,
                    height: 16,
                    top: 2,
                    left: 2,
                    transform: block.enabled ? "translateX(16px)" : "translateX(0)",
                  }}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
